import {
	EDIT_PAGE_FEATURES,
	DEFAULT_ALLOWED_FEATURES,
} from '@atlassian/embedded-confluence/features';
import { fg } from '@confluence/feature-gating';

import { useAllowedFeatures } from '../common/useAllowedFeatures.ts';
import type { FeatureMode } from '../common/useAllowedFeatures.ts';

const { edit: EDIT_PAGE_DEFAULT_ALLOWED_FEATURES } = DEFAULT_ALLOWED_FEATURES;

export enum EmbeddedEditPageButtons {
	DELETE_DRAFT = 'delete-draft',
	TEMPLATE_BROWSER = 'template-browser',
}

type TemplateBrowserConfig = { 'template-browser': { 'space-picker-disabled'?: boolean } };
type TemplateBrowserPanelConfig = {
	'template-browser-panel': { 'space-picker-disabled'?: boolean };
};
type LoomExternalOpenConfig = { 'loom-external-open': { route: string } };

export const useEmbeddedEditPageFeatures = (): {
	allowedEllipsisButtons: EmbeddedEditPageButtons[];
	// @Depracated: This is deprecated, but we need to keep it for backward compatibility.
	// Please use isTemplateBrowserPanelEnabled instead.
	isTemplateBrowserEnabled: boolean;
	isTemplateBrowserPanelEnabled: boolean;
	isBlankPageQuickActionsEnabled: boolean;
	isSpacePickerDisabled: boolean;
	hasInlineCommentsAndHighlightActions: boolean;
	isPublishModalEnabled: boolean;
	hidePublishCloseButtons: boolean;
	hideCloseButton: boolean;
	loomExternalOpenRoute: string | undefined;
	isRovoAllowed: boolean;
	showShare: boolean;
} => {
	const allowedFeatures =
		useAllowedFeatures<FeatureMode.EDIT>() || EDIT_PAGE_DEFAULT_ALLOWED_FEATURES;

	/**
	 * When allowedFeatures === all, we don't want to enable features that are suppose to disable features.
	 * For example, we want to keep "disable-publish-close-buttons" out of allowedEditPageFeatures,
	 * so that the Publish and Close buttons won't be hidden when allowedFeatures is "all".
	 * Similarly, "enable-share" is an opt-in feature and should not be enabled when allowedFeatures is "all".
	 */
	const disableFeatures = ['disable-publish-close-buttons', 'disable-close-button', 'enable-share'];

	const allAllowedFeaturesEnabled = allowedFeatures === 'all';

	const allowedEditPageFeatures = allAllowedFeaturesEnabled
		? EDIT_PAGE_FEATURES.filter((feature) => !disableFeatures.includes(feature))
		: allowedFeatures;

	const allowedEllipsisButtons: EmbeddedEditPageButtons[] = [];

	const isDeleteDraftEnabled = allowedEditPageFeatures?.includes(
		EmbeddedEditPageButtons.DELETE_DRAFT,
	);

	if (isDeleteDraftEnabled) {
		allowedEllipsisButtons.push(EmbeddedEditPageButtons.DELETE_DRAFT);
	}

	const templateBrowserConfig: TemplateBrowserConfig | undefined = !allAllowedFeaturesEnabled
		? (allowedFeatures as Array<unknown>)?.find(
				(x): x is TemplateBrowserConfig =>
					typeof x === 'object' && x !== null && 'template-browser' in x,
			)
		: undefined;
	const templateBrowserPanelConfig: TemplateBrowserPanelConfig | undefined =
		!allAllowedFeaturesEnabled && fg('confluence_embedded_decouple_template_browser_bpqa')
			? (allowedFeatures as Array<unknown>)?.find(
					(x): x is TemplateBrowserPanelConfig =>
						typeof x === 'object' && x !== null && 'template-browser-panel' in x,
				)
			: undefined;

	const isTemplateBrowserEnabled =
		allowedEditPageFeatures?.includes(EmbeddedEditPageButtons.TEMPLATE_BROWSER) ||
		!!templateBrowserConfig;
	const isTemplateBrowserPanelEnabled =
		fg('confluence_embedded_decouple_template_browser_bpqa') &&
		(allowedEditPageFeatures?.includes('template-browser-panel') || !!templateBrowserPanelConfig);

	if (isTemplateBrowserEnabled || isTemplateBrowserPanelEnabled) {
		allowedEllipsisButtons.push(EmbeddedEditPageButtons.TEMPLATE_BROWSER);
	}

	const spacePickerDisabledByTemplateBrowserPanel =
		templateBrowserPanelConfig?.['template-browser-panel']?.['space-picker-disabled'];
	const spacePickerDisabledByLegacyTemplateBrowser =
		templateBrowserConfig?.['template-browser']?.['space-picker-disabled'];

	const isSpacePickerDisabled = fg('confluence_embedded_decouple_template_browser_bpqa')
		? (spacePickerDisabledByTemplateBrowserPanel ??
			spacePickerDisabledByLegacyTemplateBrowser ??
			false)
		: (spacePickerDisabledByLegacyTemplateBrowser ?? false);

	const isBlankPageQuickActionsEnabled = allowedEditPageFeatures?.includes(
		'blank-page-quick-actions',
	);

	const hasInlineCommentsAndHighlightActions = allowedEditPageFeatures?.includes('inline-comments');

	const isPublishModalEnabled = allowedEditPageFeatures?.includes('publish-modal');

	const hidePublishCloseButtons = allowedEditPageFeatures?.includes(
		'disable-publish-close-buttons',
	);

	const hideCloseButton = allowedEditPageFeatures?.includes('disable-close-button');

	const loomExternalOpenRoute: string | undefined = (
		allowedEditPageFeatures as Array<unknown>
	)?.find(
		(x): x is LoomExternalOpenConfig =>
			typeof x === 'object' && x !== null && 'loom-external-open' in x,
	)?.['loom-external-open']?.route;

	/**
	 * Rovo chat is enabled only when:
	 * - The platform AI feature flag is on, and
	 * - The host explicitly allows it via the 'rovo' feature in allowedFeatures.
	 * We require explicit opt-in (not when allowedFeatures === 'all') so hosts
	 * must intentionally enable AI in the embedded context.
	 */
	const isRovoEditEnabled =
		!allAllowedFeaturesEnabled &&
		!!allowedEditPageFeatures?.includes('rovo') &&
		fg('jira_linked_confluence_doc_rovo_nudge_gate');

	const showShare =
		fg('csm_ai_enable_share_feature') && allowedEditPageFeatures?.includes('enable-share');

	return {
		allowedEllipsisButtons,
		// @Depracated: This is deprecated, but we need to keep it for backward compatibility.
		// Please use isTemplateBrowserPanelEnabled instead.
		isTemplateBrowserEnabled,
		isTemplateBrowserPanelEnabled,
		isBlankPageQuickActionsEnabled,
		isSpacePickerDisabled,
		hasInlineCommentsAndHighlightActions,
		isPublishModalEnabled,
		hidePublishCloseButtons,
		hideCloseButton,
		loomExternalOpenRoute,
		isRovoAllowed: isRovoEditEnabled,
		showShare,
	};
};
